/**
 * @file CustomAdminRows.tsx
 * @copyright 2023 Boston Scientific Corporation
 * ********************************************************************************************************************
 * Copyright © 2023 Boston Scientific Corporation All rights reserved.
 *
 * This file and its contents are a product of Boston Scientific Corporation.  All software distributed by Boston
 * Scientific Neuromodulation is done so under a software license.  This file may not be copied or modified without
 * execution of the appropriate software license agreement with Boston Scientific Neuromodulation or explicit written
 * permission of Boston Scientific Neuromodulation.
 *
 * This file is provided as is, with no warranties of any kind including the warranties of design, merchantability and
 * fitness for a particular purpose, or arising from a course of dealing, usage or trade practice.
 *
 * Boston Scientific Neuromodulation shall have no liability with respect to the infringement of copyrights, trade
 * secrets or any patents by this file or any part thereof.
 *
 * In no event will Boston Scientific Neuromodulation be liable for any lost revenue or profits or other special,
 * indirect and consequential damages, even if Boston Scientific has been advised of the possibility of such damages,
 * as a result of the usage of this file and software for which this file is a part.
 * ********************************************************************************************************************
 *
 */
import * as React from "react";
import TablePagination from "@mui/material/TablePagination";
import { Avatar, Button, Stack, TableBody, TableCell, TableRow, ThemeProvider, Typography } from "@mui/material";
import "./AdminHome.css"
import { GetAdminResponse } from "../../../../services/models/GetAdminResponse";
import { TablePaginationView } from "../../../common/customviews/TablePaginationView";
import { AdminRow } from "../../../../services/models/AdminRow";
import { noTextTransform, roboto500Text12 } from "../../../utils/theme/CommonTheme";
import { BaseRowComponent } from "../../base/BaseRowComponent";
import { nameColors } from "../../../utils/AppResourcesConstant";
import { CustomToolTip } from "../../../common/customviews/CustomToolTip";

/**
 * An interface definition for Component Props
 */
type CompState = {
    redraw: boolean
}

/**
 * An interface definition for Component Props
 */
interface Props {
    deleteAdmin: any;
    getAdminResponse: GetAdminResponse;
    onResendInvite: any;
}

/**
 * A class definition for component CustomAdminRows
 */
export default class CustomAdminRows extends BaseRowComponent<Props, CompState>  {

    protected loadMoreData() {
        // throw new Error("Method not implemented.");
    }

    protected shareStates () {
        this.redraw();
    }

    private redraw = ()=> {
        this.setState({
            redraw: !this.state.redraw
        });
    }

    private currentUserEmail;

    constructor(props: Props) {
        super(props)
        // eslint-disable-next-line
        this.emptyRows = this.page > 0 ? Math.max(0, (1 + this.page) * this.rowsPerPage - this.props.getAdminResponse.count!!) : 0;
        this.logger.verbose("CustomAdminRows()", CustomAdminRows.name);
        this.state = {
            redraw : true
        }

        // eslint-disable-next-line
        this.currentUserEmail = JSON.parse(this.getCurrentUser()!!).email;
    }

    /**
     * Gets names from AdminRow
     * @param row AdminRow
     * @returns string
     */
    private getName = (row: AdminRow)=> row.hcp ? `${row.hcp.firstName} ${row.hcp.lastName}` : `${row.bscRep?.firstName} ${row.bscRep?.lastName}`;

    /**
     * Get Two letters name
     * @param row AdminRow
     * @returns string
     */
    private getTwoLetterName = (row: AdminRow)=> {
        if(row.hcp) {
            const firstName = row.hcp.firstName;
            const lastName = row.hcp.lastName;
            return `${this.isEmpty(firstName) ? "N" : firstName.substring(0,1).toUpperCase()} ${this.isEmpty(firstName) ? "A" : lastName.substring(0,1).toUpperCase()}`;
        }

        if(row.bscRep){
            const firstName = row.bscRep.firstName;
            const lastName = row.bscRep.lastName;
            return `${this.isEmpty(firstName) ? "N" : firstName.substring(0,1).toUpperCase()} ${this.isEmpty(firstName) ? "A" : lastName.substring(0,1).toUpperCase()}`;
        }
    }

    /**
     * Triggers when resent invite is clicked.
     * @param adminRow AdminRow
     */
    private onResendInvite = (adminRow: AdminRow)=>{
        this.props.onResendInvite(adminRow.bscRep ? adminRow.bscRep : adminRow.hcp);
    }
    /**
     * Gets Admin Email
     * @param row AdminRow
     * @returns string
     */
    private getAdminEmail = (row: AdminRow)=>  row.hcp ? row.hcp.email : row.bscRep?.email;

    /**
     * Returns random color
     * @returns string
     */
    private getRandomColor = () => {
        this.logger.warning(`${(this.index)}`, CustomAdminRows.name);
        this.logger.warning(`${(this.index % nameColors.length)}`, CustomAdminRows.name);
        return nameColors[this.index % nameColors.length];
    }

    /**
     * Gets view for admin view
     * @param row AdminRow
     * @returns GridView
     */
    private getAdminRowView = (row: AdminRow)=> {
        this.logger.verbose(`getAdminRowView(${JSON.stringify(row.id)})`, CustomAdminRows.name);
        this.index = this.index + 1;

        return <TableRow className="table-row" key={this.getRowRandomID()}
            sx={{ background: this.getRowBackground(), borderRadius: "10px", paddingLeft: "2vh", paddingRight: "2vh", marginTop: "1vh", minWidth: "100%", width: "100%", height: "7vh", alignItems: "center" }}>
            <TableCell sx={{ width: '33.33%' }} className="TableData">
                <Stack
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    spacing={2}>
                    <Avatar sx={{ bgcolor: this.getRandomColor(), fontSize: "0.7rem" }} >{this.getTwoLetterName(row)}</Avatar>
                    <CustomToolTip title={this.getName(row)} toolTipLengthLimit={25}>
                        <Typography  sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box", width:"30vh",
                            WebkitLineClamp: "1",
                            WebkitBoxOrient: "vertical", fontSize:"0.9rem", textTransform: "capitalize"
                        }} noWrap={false}>
                            {this.getName(row)}
                        </Typography>
                    </CustomToolTip>
                </Stack>
            </TableCell>
            <TableCell className="email TableData" sx={{ paddingLeft: "2vh", width: '33.33%' }}>
                <ThemeProvider theme={roboto500Text12}>
                    <Typography>
                        {this.getAdminEmail(row)}
                    </Typography>
                </ThemeProvider>
            </TableCell>
            <TableCell sx={{ paddingLeft: "2vh", width: '33.33%' }} className="TableData">
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={2}>
                    <ThemeProvider theme={roboto500Text12}>
                        <Typography>
                            {this.getFormattedDate(row.createdAt)}
                        </Typography>
                    </ThemeProvider>
                    <ThemeProvider theme={noTextTransform}>
                        {this.currentUserEmail !== this.getAdminEmail(row) && <Button size="small" variant="contained" onClick={()=>{this.onResendInvite(row)}}
                            disableElevation className="ResendButton">
                            Re-send Invite
                        </Button> }
                    </ThemeProvider>
                    {this.currentUserEmail !== this.getAdminEmail(row) && <img src="/../delete.svg" className="delete-user" alt="" onClick={() => this.props.deleteAdmin(row)} style={{height:"2.5vh", cursor: "pointer"}} /> }
                </Stack>
            </TableCell>
        </TableRow>
    }

    render() {
        this.index = 0;
        return <React.Fragment>
            <TableBody sx={{ minWidth: 500}}>
                {(this.rowsPerPage > 0
                    ? this.props.getAdminResponse.rows!!.slice(this.page * this.rowsPerPage, this.page * this.rowsPerPage + this.rowsPerPage) //eslint-disable-line 
                    : this.props.getAdminResponse.rows!! //eslint-disable-line 
                ).map((row: AdminRow) => (
                    // eslint-disable-next-line
                    this.getAdminRowView(row)
                ))}
                {this.emptyRows > 0 && (
                    <TableRow style={{ height: 53 * this.emptyRows }}>
                        <TableCell colSpan={3} />
                    </TableRow>
                )}
                <TableRow>
                    <TablePagination
                        // rowsPerPageOptions={[5, 10, 25, { label: "All", value: -1 }]}
                        rowsPerPageOptions={[10]}

                        colSpan={3}
                        count={this.props.getAdminResponse?.count!!} //eslint-disable-line 
                        rowsPerPage={this.rowsPerPage}
                        page={this.page}
                        SelectProps={{
                            inputProps: {
                                "aria-label": "rows per page",
                            },
                            native: true,
                        }}
                        onPageChange={this.handleChangePage}
                        onRowsPerPageChange={this.handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationView}
                    />
                </TableRow>
            </TableBody>

          
        </React.Fragment>
    }
}
